import { FC, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useStudentDetailsDocumentsHook,
} from '../../../../../../apis/students/studentDetails/useStudentDetailsService';
import PaginatedGrid from '../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Download,
  PictureAsPdf,
  Image,
  Description,
  InsertDriveFile,
  Search,
  Clear,
} from '@mui/icons-material';

interface StudentDocumentsTabProps {
  studentId: string;
}

const StudentDocumentsTab: FC<StudentDocumentsTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, error } = useStudentDetailsDocumentsHook(
    studentId,
    {
      page,
      limit: pageSize,
      ...(categoryFilter !== 'all' && { category: categoryFilter }),
      ...(typeFilter !== 'all' && { type: typeFilter }),
    }
  );

  const documents = useMemo(() => data?.data || [], [data]);

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(
      (doc) =>
        doc.documentName.toLowerCase().includes(query) ||
        doc.documentType.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(documents.map((d) => d.documentType))
    );
    return uniqueCategories.sort();
  }, [documents]);

  const types = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(documents.map((d) => d.documentType))
    );
    return uniqueTypes.sort();
  }, [documents]);

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <PictureAsPdf sx={{ color: 'error.main' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image sx={{ color: 'primary.main' }} />;
      case 'doc':
      case 'docx':
        return <Description sx={{ color: 'info.main' }} />;
      default:
        return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
    }
  };

  const handleDownload = (doc: any) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'documentType',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return getFileIcon(params.value as string);
        },
      },
      {
        field: 'documentName',
        headerName: translateText('students.studentDetails.documents.columns.title'),
        width: 250,
        flex: 1,
      },
      {
        field: 'documentType',
        headerName: translateText('students.studentDetails.documents.columns.type'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Chip
              label={params.value}
              size="small"
              variant="outlined"
              color="primary"
            />
          );
        },
      },
      {
        field: 'submissionDate',
        headerName: translateText('students.studentDetails.documents.columns.uploadDate'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        },
      },
      {
        field: 'status',
        headerName: translateText('common.status'),
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.value as string;
          const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
            verified: 'success',
            pending: 'warning',
            rejected: 'error',
          };
          return (
            <Chip
              label={status}
              size="small"
              color={colorMap[status.toLowerCase()] || 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: translateText('common.actions'),
        width: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const document = params.row;
          return (
            <Tooltip title={translateText('students.studentDetails.documents.download')}>
              <IconButton
                size="small"
                onClick={() => handleDownload(document)}
                data-testid={`download-doc-btn-${document.documentId}`}
              >
                <Download />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [translateText]
  );

  const rows = useMemo(() => {
    return filteredDocuments.map((doc) => ({
      id: doc.documentId,
      documentName: doc.documentName,
      documentType: doc.documentType,
      submissionDate: doc.submissionDate,
      status: doc.status,
      fileUrl: doc.fileUrl,
      documentId: doc.documentId,
    }));
  }, [filteredDocuments]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!studentId) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-documents-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="info" data-testid="student-documents-tab-no-student">
            {translateText('students.studentDetails.documents.noStudentId')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-documents-tab-container"
      >
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
            data-testid="student-documents-tab-loading"
          >
            <CircularProgress />
            <Typography
              variant="body2"
              sx={{ ml: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-documents-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="error" data-testid="student-documents-tab-error">
            {translateText('common.error')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{ p: { xs: 1, sm: 2 } }}
      data-testid="student-documents-tab-container"
    >
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}
        >
          {translateText('students.studentDetails.documents.title')}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="student-documents-tab-filters"
        >
          <TextField
            fullWidth
            placeholder={translateText('students.studentDetails.documents.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    data-testid="student-documents-tab-search-clear"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            data-testid="student-documents-tab-search"
          />
          {categories.length > 0 && (
            <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <ControlledSelectField
                label={translateText('students.studentDetails.documents.filters.category')}
                id="category-filter"
                name="categoryFilter"
                value={categoryFilter}
                onChangeHandler={(e) => setCategoryFilter(e.target.value)}
                itemList={[
                  { key: 'all', value: translateText('common.all') },
                  ...categories.map((category) => ({ key: category, value: category })),
                ]}
                isGridItem={false}
                dataTestId="student-documents-tab-category-filter"
              />
            </Box>
          )}
          {types.length > 0 && (
            <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <ControlledSelectField
                label={translateText('students.studentDetails.documents.columns.type')}
                id="type-filter"
                name="typeFilter"
                value={typeFilter}
                onChangeHandler={(e) => setTypeFilter(e.target.value)}
                itemList={[
                  { key: 'all', value: translateText('common.all') },
                  ...types.map((type) => ({ key: type, value: type })),
                ]}
                isGridItem={false}
                dataTestId="student-documents-tab-type-filter"
              />
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        {filteredDocuments.length === 0 && !isLoading ? (
          <Alert severity="info" data-testid="student-documents-tab-no-documents">
            {searchQuery
              ? translateText('students.studentDetails.documents.noSearchResults')
              : translateText('students.studentDetails.documents.noDocuments')}
          </Alert>
        ) : (
          <PaginatedGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            dataTestId="student-documents-tab-grid"
            serverSide={false}
            totalCount={filteredDocuments.length}
            currentPage={page}
            currentPageSize={pageSize}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default StudentDocumentsTab;
